<template lang="pug">
div.document-repository
  // Botón único externo
  b-button(
    :variant="readOnly ? 'success' : uploadVariant"
    :disabled="disabled ? true : readOnly ? !documentsCount : false"
    :size="size"
    @click="openModal"
  )
    | {{ `${$t(`message.${readOnly ? 'see' : 'upload'}`)}${buttonExtraText ? ` ${$t(buttonExtraText)}` : ''}` }}
    b-badge.ml-1(
      v-if="readOnly || documentsCount"
      :variant="badgeVariant"
    )
      | {{ documentsCount }}

  // Modal repositorio
  b-modal(
    v-model="showModal"
    :title="titleLabel"
    size="lg"
    scrollable
  )
    // Acciones dentro del modal
    div.d-flex.mb-2

      b-button(
        v-if="readOnly"
        variant="success"
        :disabled="isDownloadingAllFiles"
        @click="downloadAllFiles"
      )
        feather-icon(
          icon="DownloadIcon"
          class="mr-50"
        )
        | {{ $t(type === 'evidence' ? 'message.downloadAllEvidences' : 'message.downloadAllDocuments') }} (.zip)

      b-button(
        v-else
        variant="primary"
        @click="triggerFileInput"
      )
        feather-icon(
          icon="PlusIcon"
          class="mr-50"
        )
        | {{ $t(type === 'evidence' ? 'message.Add_Evidences' : 'message.Add_Documents') }}

    input(
      ref="fileSelector"
      type="file"
      class="d-none"
      multiple
      :accept="inputFileTypes"
      @change="onFilesSelected"
    )

    // Listado
    b-table(
      small
      bordered
      responsive
      :items="localDocuments"
      :fields="fields"
      :empty-text="$t(type === 'evidence' ? 'message.no_evidences' : 'message.no_documents')"
    )
      template(#cell(actions)="row")
        view-button(v-if="row.item.key ? canBeViewed(row.item.key) : canPreviewDirectly(row.item.name)" variant="success" placement="bottom" @clicked="handleView(row.item)")
        download-button(placement="bottom" @clicked="handleView(row.item, true)")
        delete-button(v-if="!row.item.isUploaded || allowDeleteKeys" placement="bottom" @clicked="removeOne(row.index, row.item.isUploaded, row.item.key)")

    // Footer del modal
    template(#modal-footer)
      b-button(
        variant="secondary"
        @click="showModal = false"
      )
        | {{ $t('message.close') }}
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api'
import { BButton, BModal, BFormCheckbox, BFormInput, BTable, BBadge } from "bootstrap-vue";
import i18n from '@/libs/i18n'
import ViewButton from '@/views/components/Shared/Buttons/ViewButton.vue'
import DownloadButton from '@/views/components/Shared/Buttons/DownloadButton.vue'
import DeleteButton from '@/views/components/Shared/Buttons/DeleteButton.vue'
import useCommon from '@/views/organization/useCommon'
import { inputFileTypes } from '@/constants'

export default {
  components: {
    BButton,
    BModal,
    BFormCheckbox,
    BFormInput,
    BTable,
    BBadge,
    ViewButton,
    DownloadButton,
    DeleteButton,
  },
  props: {
    documents: {
      type: Array,
      required: true,
    },
    keys: {
      type: Array,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'document',
    },
    size: {
      type: String,
      default: 'md',
    },
    uploadVariant: {
      type: String,
      default: 'info',
    },
    badgeVariant: {
      type: String,
      default: 'warning',
    },
    buttonExtraText: {
      type: String,
      default: '',
    },
    allowDeleteKeys: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const { openDocument, canBeViewed, canPreviewDirectly, openLocalFile, downloadFile, downloadZip, getCleanFileName } = useCommon()
    const showModal = ref(false)
    const fileSelector = ref(null)
    const localDocuments = ref([])
    const isDownloadingAllFiles = ref(false)

    const fields = [
      { key: 'name', label: i18n.t('message.tableHeader.name') },
      { key: 'actions', label: i18n.t('message.tableHeader.actions') },
    ]

    const documentsCount = computed(() => (props.keys?.length || 0) + (props.documents?.length || 0))

    const titleLabel = computed(() =>
      documentsCount.value
        ? `${i18n.t(props.type === 'evidence' ? 'Evidences' : 'Documents')} (${documentsCount.value})`
        : i18n.t(props.type === 'evidence' ? 'Evidences' : 'Documents')
    )

    const openModal = () => {
      showModal.value = true
    }

    const triggerFileInput = () => {
      fileSelector.value.click()
    }

    const onFilesSelected = (e) => {
      const newDocs = Array.from(e.target.files)
      emit('add', newDocs)
      e.target.value = null
    }

    const removeOne = (index, isUploaded, key) => {
      emit('delete', index, isUploaded, key)
    }

    const handleView = (item, isDownload = false) => {
      // ⭐ archivo local
      if (!item.isUploaded && item.file) {

        if (isDownload) {
          downloadFile({ file: item.file })
          return
        }

        openLocalFile(item.file)

        return
      }

      // ⭐ archivo remoto
      openDocument(item.key, isDownload)

      emit('evidence-viewed', item.key)
    }

    const downloadAllFiles = async () => {
      if (!props.keys?.length) return

      try {
        isDownloadingAllFiles.value = true
        await downloadZip(props.keys, props.type)
  
        props.keys.forEach(key => {
          emit('evidence-viewed', key)
        })

      } catch (error) {
        console.log(error)
      } finally {
        isDownloadingAllFiles.value = false
      }
    }

    watch(
      () => props.documents,
      (documents) => {
        const updatedDocuments = documents.map(file => ({
          name: file.name,
          isUploaded: false,
          file,
        }))
        const actualKeys = localDocuments.value.filter(doc => doc.isUploaded)

        localDocuments.value = [...updatedDocuments, ...actualKeys]
      },
      { immediate: true }
    )

    watch(
      () => props.keys,
      (keys) => {
        const updatedKeys = keys.map(key => ({
          name: getCleanFileName(key),    // Obtener el nombre del archivo
          key,
          isUploaded: true,
        }))
        const actualDocuments = localDocuments.value.filter(doc => !doc.isUploaded)

        localDocuments.value = [...updatedKeys, ...actualDocuments]
      },
      { immediate: true }
    )

    return {
      showModal,
      fileSelector,
      fields,
      openModal,
      triggerFileInput,
      onFilesSelected,
      removeOne,
      handleView,
      downloadAllFiles,
      localDocuments,
      documentsCount,
      titleLabel,
      inputFileTypes,
      canBeViewed,
      canPreviewDirectly,
      isDownloadingAllFiles,
    }
  },
}
</script>